import { useState, useEffect } from 'react'
import { api, formatDate } from '../api'
import CheckoutPayment from './CheckoutPayment'
import { useTheme } from '../theme'

// Checkout commande client (inc.4, §5 ScreenFinaliser) : retrait/livraison + avoirs -> POST /client/orders
// (cree commande + PaymentIntent) -> paiement carte (Payment Element) ou 100% avoirs -> confirmation.

const VERT = 'rgb(var(--c-green))'
const ORANGE = 'rgb(var(--c-primary))'
const TEAL = 'rgb(var(--c-teal))'
const formatPrix = (n) => `${Number(n).toFixed(2).replace('.', ',')} €`

function SectionLabel({ children, color = TEAL }) {
  return <p className="text-[11px] font-bold uppercase tracking-[0.16em] mt-4 mb-2.5" style={{ color }}>{children}</p>
}

export default function TourneeCheckout({ tour, items, sousTotal, isAuthenticated, onNavigate, onBack, onPlaced, onGoCommandes }) {
  const { isWeb3 } = useTheme()
  const tc = 'text-ink'
  const ts = 'text-ink/60'
  const glass = 'panneau-verre'
  const inputCls = `w-full text-[15px] px-3 py-2.5 rounded-xl mb-2 box-border ${isWeb3 ? 'bg-white/10 text-white placeholder-white/40 border border-white/10' : 'bg-white/70 text-ink border border-ink/15'}`

  const [step, setStep] = useState('form') // form | paiement | confirm
  const [pickupPoints, setPickupPoints] = useState([])
  const [retraitMode, setRetraitMode] = useState('pickup') // pickup | livraison
  const [pickupId, setPickupId] = useState('')
  const [adresse, setAdresse] = useState({ rue: '', codePostal: '', ville: '', instructions: '' })
  const [avoirSolde, setAvoirSolde] = useState(0)
  const [useAvoir, setUseAvoir] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')
  const [paiement, setPaiement] = useState(null) // { clientSecret, publishableKey, montantCarte }
  const [placedOrder, setPlacedOrder] = useState(null)

  useEffect(() => {
    if (!isAuthenticated) return
    api.getPickupPoints().then((d) => {
      const list = Array.isArray(d) ? d : []
      setPickupPoints(list)
      if (list.length === 0) setRetraitMode('livraison')
    }).catch(() => setPickupPoints([]))
    api.getWallet().then((w) => setAvoirSolde(Math.max(0, Number(w?.solde?.client || 0)))).catch(() => {})
  }, [isAuthenticated])

  const avoirApplique = useAvoir ? Math.min(avoirSolde, sousTotal) : 0
  const totalCarte = Math.max(0, sousTotal - avoirApplique)

  const formValide = retraitMode === 'pickup'
    ? !!pickupId
    : (adresse.rue.trim() && adresse.codePostal.trim() && adresse.ville.trim())

  const submit = async () => {
    setCreating(true)
    setError('')
    try {
      const dto = {
        tourId: tour.id,
        items: items.map((it) => ({ tourOfferId: it.offerId, quantite: it.q })),
        montantAvoir: avoirApplique > 0 ? avoirApplique : undefined,
        ...(retraitMode === 'pickup'
          ? { pickupPointId: pickupId }
          : { livraisonRue: adresse.rue, livraisonCodePostal: adresse.codePostal, livraisonVille: adresse.ville, livraisonInstructions: adresse.instructions || undefined }),
      }
      const resp = await api.createClientOrder(dto)
      setPlacedOrder(resp.order)
      const p = resp.paiement || {}
      if (p.requiresPayment && p.clientSecret) {
        setPaiement({ clientSecret: p.clientSecret, publishableKey: p.publishableKey, montantCarte: Number(p.montantCarte ?? totalCarte) })
        setStep('paiement')
      } else {
        // Regle 100% avoirs : commande confirmee sans carte.
        onPlaced?.()
        setStep('confirm')
      }
    } catch (e) {
      setError(e.message || 'La commande n’a pas pu être créée.')
    } finally {
      setCreating(false)
    }
  }

  // ===== Non connecte : la commande exige un compte (la commande invite = chantier ulterieur) =====
  if (!isAuthenticated) {
    return (
      <div>
        <button onClick={onBack} className={`text-sm font-semibold mb-3 ${ts}`}>‹ Panier</button>
        <div className={`rounded-3xl p-7 text-center ${glass}`}>
          <span className="text-4xl block mb-2">🦊</span>
          <p className="font-serif text-[20px] mb-1.5" style={{ color: isWeb3 ? '#fff' : VERT }}>Un compte pour commander</p>
          <p className={`text-[13px] mb-4 ${ts}`}>Crée ton compte (ou connecte-toi) pour finaliser ta commande et la suivre. Ton panier est gardé.</p>
          <button onClick={() => onNavigate?.('connexion')} className="px-7 py-3 rounded-full text-white text-sm font-extrabold" style={{ background: 'linear-gradient(120deg, rgba(244,137,83,0.9), rgba(224,101,58,0.92))' }}>Se connecter</button>
        </div>
      </div>
    )
  }

  // ===== Confirmation =====
  if (step === 'confirm') {
    return (
      <div className="text-center animate-fade-up">
        <div className={`rounded-[28px] p-7 ${glass}`}>
          <span className="text-[64px] block">🦊</span>
          <h1 className="font-serif text-[26px] mt-2" style={{ color: isWeb3 ? '#fff' : VERT }}>Commande validée !</h1>
          <p className={`text-[13.5px] mt-2 leading-relaxed ${ts}`}>
            {tour.dateLivraison ? <>Livraison le <strong>{formatDate(tour.dateLivraison)}</strong>. </> : ''}
            {placedOrder?.visibleId ? <>Réf. {placedOrder.visibleId}. </> : ''}
            Tu peux suivre son avancement dans « Mes commandes ».
          </p>
          <div className="flex gap-2.5 justify-center mt-5">
            <button onClick={onBack} className={`px-5 py-3 rounded-full font-bold text-[13px] ${glass} ${tc}`}>Continuer</button>
            <button onClick={onGoCommandes} className="px-5 py-3 rounded-full font-extrabold text-[13px]" style={{ background: ORANGE, color: VERT }}>Mes commandes</button>
          </div>
        </div>
      </div>
    )
  }

  // ===== Paiement carte =====
  if (step === 'paiement') {
    return (
      <div>
        <button onClick={() => setStep('form')} className={`text-sm font-semibold mb-3 ${ts}`}>‹ Modifier</button>
        <h1 className="font-serif text-[24px] mb-1" style={{ color: isWeb3 ? '#fff' : VERT }}>Paiement sécurisé</h1>
        <p className={`text-[12.5px] mb-4 ${ts}`}>Carte bancaire — {formatPrix(paiement?.montantCarte ?? totalCarte)}{avoirApplique > 0 ? ` (après ${formatPrix(avoirApplique)} d'avoirs)` : ''}.</p>
        <CheckoutPayment
          clientSecret={paiement?.clientSecret}
          publishableKey={paiement?.publishableKey}
          amount={paiement?.montantCarte ?? totalCarte}
          onBack={() => setStep('form')}
          onSuccess={() => { onPlaced?.(); setStep('confirm') }}
        />
      </div>
    )
  }

  // ===== Formulaire (retrait + avoirs + recap) =====
  return (
    <div>
      <button onClick={onBack} className={`text-sm font-semibold mb-3 ${ts}`}>‹ Panier</button>
      <h1 className="font-serif text-[26px]" style={{ color: isWeb3 ? '#fff' : VERT }}>Finaliser</h1>

      <SectionLabel>Comment récupérer ?</SectionLabel>
      <div className="flex gap-2 mb-2">
        {[{ k: 'pickup', e: '🏪', l: 'Point de retrait' }, { k: 'livraison', e: '🚚', l: 'Livraison' }].map((m) => (
          <button key={m.k} onClick={() => setRetraitMode(m.k)} className={`flex-1 rounded-[16px] py-3 text-center ${glass}`} style={retraitMode === m.k ? { border: `1.5px solid ${ORANGE}` } : undefined}>
            <div className="text-[20px]">{m.e}</div>
            <p className={`text-[12.5px] font-bold mt-0.5 ${tc}`}>{m.l}</p>
          </button>
        ))}
      </div>

      {retraitMode === 'pickup' ? (
        pickupPoints.length === 0 ? (
          <p className={`text-[12.5px] ${ts}`}>Aucun point de retrait actif pour le moment — choisis la livraison.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {pickupPoints.map((p) => (
              <button key={p.id} onClick={() => setPickupId(p.id)} className={`text-left rounded-[16px] px-4 py-3 ${glass}`} style={pickupId === p.id ? { border: `1.5px solid ${ORANGE}` } : undefined}>
                <p className={`text-[13.5px] font-bold ${tc}`}>{p.nom}</p>
                <p className={`text-[11.5px] ${ts}`}>{[p.adresseRue, p.codePostal, p.ville].filter(Boolean).join(', ')}</p>
              </button>
            ))}
          </div>
        )
      ) : (
        <div className={`rounded-[18px] p-4 ${glass}`}>
          <input className={inputCls} placeholder="Adresse" value={adresse.rue} onChange={(e) => setAdresse((a) => ({ ...a, rue: e.target.value }))} />
          <div className="flex gap-2">
            <input className={inputCls + ' w-1/3'} placeholder="CP" inputMode="numeric" value={adresse.codePostal} onChange={(e) => setAdresse((a) => ({ ...a, codePostal: e.target.value }))} />
            <input className={inputCls + ' flex-1'} placeholder="Ville" value={adresse.ville} onChange={(e) => setAdresse((a) => ({ ...a, ville: e.target.value }))} />
          </div>
          <input className={inputCls + ' mb-0'} placeholder="Instructions (optionnel)" value={adresse.instructions} onChange={(e) => setAdresse((a) => ({ ...a, instructions: e.target.value }))} />
        </div>
      )}

      {avoirSolde > 0 && (
        <>
          <SectionLabel>Mes avoirs</SectionLabel>
          <button onClick={() => setUseAvoir((v) => !v)} className={`w-full flex items-center gap-3 rounded-[18px] px-4 py-3 ${glass}`}>
            <span className="text-[22px]">👛</span>
            <div className="flex-1 text-left">
              <p className={`text-[13px] font-bold ${tc}`}>{formatPrix(avoirSolde)} disponibles</p>
              <p className={`text-[11px] ${ts}`}>{useAvoir ? `Appliqués : −${formatPrix(avoirApplique)}` : 'Conservés pour plus tard'}</p>
            </div>
            <span className="w-11 h-6 rounded-full relative flex-shrink-0" style={{ background: useAvoir ? TEAL : 'rgba(7,62,61,0.18)' }}>
              <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all" style={{ left: useAvoir ? 22 : 2 }} />
            </span>
          </button>
        </>
      )}

      <div className={`rounded-[22px] p-4 mt-4 ${glass}`}>
        <div className="flex justify-between text-[13px]"><span className={ts}>Sous-total</span><span className={tc}>{formatPrix(sousTotal)}</span></div>
        {avoirApplique > 0 && <div className="flex justify-between text-[13px] mt-1"><span style={{ color: TEAL }}>Avoirs</span><span style={{ color: TEAL }}>−{formatPrix(avoirApplique)}</span></div>}
        <div className="flex justify-between mt-2 pt-2 border-t border-ink/10">
          <span className={`font-bold ${tc}`}>{totalCarte > 0 ? 'À payer par carte' : 'Total'}</span>
          <span className={`font-extrabold ${tc}`}>{formatPrix(totalCarte)}</span>
        </div>
      </div>

      {error && <p className="text-[12.5px] mt-3 font-semibold" style={{ color: '#d4503a' }}>{error}</p>}

      <button onClick={submit} disabled={!formValide || creating}
        className="mt-4 w-full py-4 rounded-full text-[15px] font-extrabold text-creme disabled:opacity-50"
        style={{ background: VERT, boxShadow: '0 14px 32px rgba(7,62,61,0.3)' }}>
        {creating ? 'Création…' : totalCarte > 0 ? `Payer ${formatPrix(totalCarte)}` : 'Confirmer la commande'}
      </button>
    </div>
  )
}
